import axios from 'axios'
import { Safety } from '../classes/Safety'
import { SystemControl } from '../classes/SystemControl'
import { API_VERSION } from '../hooks/useAxios'
import { LoginResponse, Token } from '../types/auth'
import { VERSION } from '../version'
import { trySessionRefresh } from './auth'

/**
 * How long the first page load of a new build waits for `/auth/me` before giving up.
 * Nothing renders meanwhile.
 */
const ME_TIMEOUT_MS = 10000

/**
 * Pause before the one retry of `/auth/me` after a failure that was not a refusal.
 */
const ME_RETRY_DELAY_MS = 1000

/**
 * Name of the lock that lets one tab at a time carry the session over.
 */
const LOCK_NAME = 'enion-adopt-session'

/**
 * Outcome of `/auth/me`: the data, a refusal of the token (401/403), or a failure that
 * says nothing about the session (no response, timeout, aborted, 5xx).
 */
type MeResult = LoginResponse | 'refused' | 'failed'

async function fetchMeOnce(token: Token): Promise<MeResult> {
  return axios({
    url: `/api/${API_VERSION}/auth/me`,
    headers: { Authorization: `Bearer ${token}` },
    timeout: ME_TIMEOUT_MS,
  })
    .then((res): MeResult => res.status && res.data && res.data.user ? res.data as LoginResponse : 'failed')
    .catch((err): MeResult => err.response && [401, 403].includes(err.response.status) ? 'refused' : 'failed')
}

/**
 * Fetch the session data with the given token, trying once more if the failure was
 * not a refusal: the reload happens right when a deploy is restarting the server.
 */
async function fetchMe(token: Token): Promise<MeResult> {
  const me = await fetchMeOnce(token)
  if (me !== 'failed') {
    return me
  }
  await new Promise(resolve => setTimeout(resolve, ME_RETRY_DELAY_MS))
  return fetchMeOnce(token)
}

/**
 * Run the function while no other tab is running it. After a deploy all the tabs
 * reload at once, and a token refresh takes the refresh token out of storage while it
 * is in flight: a second tab starting meanwhile would find no session at all.
 * Browsers without Web Locks run it unguarded.
 */
async function oneTabAtATime(fn: () => Promise<void>): Promise<void> {
  if (navigator.locks) {
    await navigator.locks.request(LOCK_NAME, fn)
  } else {
    await fn()
  }
}

async function adopt(): Promise<void> {
  if (Safety.version() === VERSION) {
    // Another tab did it while this one waited for its turn. The device collection
    // read the old cache when its module loaded.
    SystemControl.setDevices(Safety.get('devices', []))
    return
  }

  const token = Safety.get<Token | undefined>('token', undefined)
  let me: MeResult = token ? await fetchMe(token) : 'refused'
  if (me === 'refused') {
    const refreshed = await trySessionRefresh()
    if (refreshed) {
      me = await fetchMe(refreshed)
    } else if (localStorage.getItem('refresh_token')) {
      // The refresh failed without the server refusing the token, which was put back.
      me = 'failed'
    }
  }

  if (me === 'refused') {
    Safety.clear()
    SystemControl.setDevices([])
    return
  }

  Safety.clearCache()

  if (me === 'failed') {
    // The server could not be asked. Keep the tokens but do not mark the storage as
    // this build's: this load has no session data and shows the login page, the next
    // one tries again.
    SystemControl.setDevices([])
    return
  }

  // Stored in the same shape as `useAuth().updateMe()` stores them.
  Safety.set('user', { ...me.user, features: me.features, announcements: me.announcements })
  Safety.set('locations', me.locations.length ? me.locations : undefined)
  // The device collection read the old cache when its module loaded.
  SystemControl.setDevices(me.devices)
  Safety.setVersion()
}

/**
 * Carry the session over to a newly deployed build. Run once before the app renders.
 *
 * The storage holds, besides the tokens, data cached by the build that wrote it (user,
 * locations, devices, drafts). A new build must not render from that, so on a version
 * change the cache is dropped and fetched again from `/auth/me` with the stored token
 * (refreshed if it has expired). The session is dropped only when the server refuses
 * it. Before, every version change dropped it: the user had to log in after each deploy.
 */
export async function adoptSessionForNewBuild(): Promise<void> {
  if (Safety.version() === VERSION) {
    return
  }
  try {
    await oneTabAtATime(adopt)
  } catch (err) {
    console.error(err)
    Safety.clear()
    SystemControl.setDevices([])
  }
}
